/**
 * useFileUpload Hook
 *
 * Manages file upload operations including Firebase Storage uploads,
 * platform-specific media uploads, and file validation state management.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import Mime from 'mime';

// Models
import { Room, Collaboration } from 'src/app/models/Chat';
import { chatFiles } from 'src/app/models/Files';

// Database
import { addChatFiles, getFileByMediaSHA256 } from 'src/db';
import { createRef } from 'src/db/connection';
import { uploadFileToStorage } from 'src/api/firebase/storage';

// Utils
import { calculateFileSHA256 } from 'src/util/FileUtil';
import {
  createFileDataByUploadResult,
  getVideoCover,
  getVideoDetails,
} from 'src/util/video';
import { checkFile, formatSize } from 'src/app/modules/chat/utils';
import * as lc from 'src/app/modules/localstorage/index';

// Types
type FileMediaData =
  | (chatFiles & { id?: string })
  | { url?: string; id?: string };

export interface UseFileUploadParams {
  selectedRoom: Room | undefined;
  selectedCollaboration: Collaboration;
}

export interface UseFileUploadReturn {
  file: File | undefined;
  fileSize: string;
  inputFileName: string;
  messageType: string | undefined;
  setFile: (file: File | undefined) => void;
  setFileSize: (size: string) => void;
  setInputFileName: (name: string) => void;
  setMessageType: (type: string | undefined) => void;
  uploadFile: (companyID: string) => Promise<FileMediaData | undefined>;
  uploadMediaFallback: () => Promise<{ url?: string; id?: string } | undefined>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: () => void;
}

export const useFileUpload = ({
  selectedRoom,
  selectedCollaboration,
}: UseFileUploadParams): UseFileUploadReturn => {
  const { t } = useTranslation();

  // File state
  const [file, setFile] = useState<File>();
  const [fileSize, setFileSize] = useState<string>('');
  const [inputFileName, setInputFileName] = useState<string>('');
  const [messageType, setMessageType] = useState<string | undefined>('text');

  /**
   * Build platform-specific media upload endpoint (v2 API format)
   */
  const buildMediaUploadEndpoint = useCallback((): {
    endpoint: string | null;
    platform: string;
  } => {
    const channel = selectedRoom?.channel;
    if (!channel) return { endpoint: null, platform: '' };

    const platform = channel.toLowerCase();

    // Extract platform-specific sender/identifier based on channel type
    let sender: string | null = null;

    switch (platform) {
      case 'whatsapp':
        // WhatsApp: use phone number as sender
        sender =
          selectedRoom?.userSearchKey ||
          selectedCollaboration?.phoneNumber ||
          null;
        break;

      case 'shopee':
      case 'lazada':
      case 'tiktok':
        // Marketplaces: use account ID or shop ID as sender
        sender =
          selectedRoom?.account?.id ||
          (selectedCollaboration as any)?.shopId ||
          null;
        break;

      default:
        sender = null;
    }

    const endpoint = sender ? `/v2/${platform}/${sender}/media` : null;
    return { endpoint, platform };
  }, [
    selectedRoom?.channel,
    selectedRoom?.userSearchKey,
    selectedRoom?.account?.id,
    selectedCollaboration?.phoneNumber,
  ]);

  /**
   * Platform media upload fallback (when Firebase fails)
   */
  const uploadMediaFallback = useCallback(async (): Promise<
    { url?: string; id?: string } | undefined
  > => {
    if (!file) return undefined;

    const { endpoint, platform } = buildMediaUploadEndpoint();
    if (!endpoint) {
      console.error(
        'Could not determine media upload endpoint for channel:',
        selectedRoom?.channel
      );
      return undefined;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('messaging_product', platform);
      formData.append('type', file.type.split('/')[0]); // image, video, audio, document

      console.log(`Uploading media to platform endpoint: ${endpoint}`);

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        console.error(
          `Platform media upload failed (${response.status}):`,
          response.statusText
        );
        return undefined;
      }

      const data = await response.json();

      // Map response based on platform response format
      // Normalize all platform responses to { url, id }
      const mediaUrl =
        data.response?.mediaUrl ||
        data.response?.url ||
        data.mediaUrl ||
        data.url ||
        undefined;

      const fileId = data.response?.id || data.id || undefined;

      console.log('Platform media upload successful:', { mediaUrl, fileId });

      return { url: mediaUrl, id: fileId };
    } catch (error) {
      console.error('Platform media upload error:', error);
      return undefined;
    }
  }, [file, buildMediaUploadEndpoint, selectedRoom?.channel]);

  /**
   * File upload handler (Firebase Storage)
   */
  const uploadFile = useCallback(
    async (companyID: string) => {
      if (!file) return;

      const clientID = lc.getItemLC(lc.LCName.Client)?.id;
      if (!clientID) {
        alert('client Id not found');
        setInputFileName('client Id not found');
        setFile(undefined);
        return;
      }

      const fileSHA256 = await calculateFileSHA256(file);
      const fileChannel = selectedRoom?.channel;

      if (!fileSHA256 || !fileChannel) return;

      const fileExist = await getFileByMediaSHA256(
        fileSHA256,
        fileChannel,
        clientID
      );
      if (fileExist) return fileExist;

      const fileName = `${file.name.split('.')[0]}_${Date.now()}`;
      const fileMimeType = file.type;
      const fileExtension = Mime.getExtension(fileMimeType);
      const fileTypeRaw = file.type.split('/')[0];
      const fileType =
        fileTypeRaw === 'image' ||
        fileTypeRaw === 'video' ||
        fileTypeRaw === 'audio'
          ? fileTypeRaw
          : 'document';

      const storagePath = `${clientID}/${fileType}s/sent/${fileName}${
        fileExtension ? `.${fileExtension}` : ''
      }`;
      const metadata = { contentType: fileMimeType, fileName };

      const uploadResult = await uploadFileToStorage(
        storagePath,
        file,
        metadata
      );

      if (!uploadResult) {
        // Firebase upload failed - return undefined so calling code can handle via API
        console.warn(
          'Firebase storage upload failed, file will be processed by API'
        );
        return undefined;
      }

      const FileDataChat: chatFiles = {
        client: createRef('clients', clientID),
        name: uploadResult.metaData.name,
        url: uploadResult.mediaUrl,
        storage_path: uploadResult.metaData.fullPath,
        size: uploadResult.metaData.size,
        type: fileType,
        media_channel: fileChannel,
        media_sha256: fileSHA256,
        mime_type: uploadResult.metaData.contentType ?? fileMimeType ?? '',
        extension: fileExtension ?? '',
        createdAt: firebase.firestore.Timestamp.now(),
        updatedAt: firebase.firestore.Timestamp.now(),
      };

      if (fileType === 'video') {
        const videoDetail = await getVideoDetails(file);
        const videoCover = await getVideoCover(file);
        if (videoCover) {
          const coverFile = await createFileDataByUploadResult(videoCover, {
            clientId: clientID,
            channel: FileDataChat.media_channel,
          });
          if (coverFile) {
            FileDataChat.media_preview_id = coverFile.id;
            FileDataChat.media_width = videoDetail.width;
            FileDataChat.media_height = videoDetail.height;
          }
        }
      }

      const addFileResult = await addChatFiles(FileDataChat);
      return { ...FileDataChat, id: addFileResult?.id };
    },
    [file, selectedRoom?.channel, t]
  );

  /**
   * File select handler
   */
  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
      }
    },
    []
  );

  /**
   * Remove file handler
   */
  const removeFile = useCallback(() => {
    setFile(undefined);
    setFileSize('');
    setInputFileName('');
    setMessageType('text');
  }, []);

  /**
   * File validation effect
   * Validates file type, size, and extension when file changes
   */
  useEffect(() => {
    if (file) {
      const size = file.size;
      setInputFileName(file.name);
      setFileSize(formatSize(size));

      const tempArrFileType = file.type;
      const arryFileType = tempArrFileType.split('/');

      if (!arryFileType?.length || !arryFileType[0]) {
        alert(t('HC.Error.InvalidFiletype'));
        setInputFileName(t('HC.Error.InvalidFiletype'));
        setFile(undefined);
        return;
      }

      const fileType = arryFileType[0];
      const fileExtension = arryFileType[1];
      const [result, error, maxSize] = checkFile(
        fileType,
        fileExtension,
        size,
        selectedRoom?.channel
      );

      if (error) {
        const errorMsg =
          maxSize !== 0
            ? t('HC.File.' + error).replace('<<size>>', maxSize + ' MB')
            : t('HC.Error.' + error);
        alert(errorMsg);
        setInputFileName(errorMsg);
        setFile(undefined);
        return;
      }

      if (result) {
        setMessageType(result.toString());
      }
    }
  }, [file, selectedRoom?.channel, t]);

  return {
    file,
    fileSize,
    inputFileName,
    messageType,
    setFile,
    setFileSize,
    setInputFileName,
    setMessageType,
    uploadFile,
    uploadMediaFallback,
    handleFileSelect,
    removeFile,
  };
};
